import type { AnnotationPromptObject } from "@/api";

/**
 * 제품 SAM 트래킹 직결 WS 프로토콜의 순수 계층. 다음을 담당한다:
 * - 메시지 파싱, 청크 계산, 컨디셔닝 echo 판정, 에러 문구, 결과 캐시 키.
 * WS I/O 와 청크 루프 실행은 infra 의 드라이버가 소유한다.
 *
 * 프로토콜은 단방향 스트림이다. 클라이언트가 init 을 1회 보내면 서버가 start → frame* → done 을
 * 보낸다. 취소 수단은 WS close 뿐이다. 서버 폴리곤 좌표는 원본 프레임 natural pixel 정수 [x, y]다.
 */

// holder 의 max_frames 상한. 이보다 긴 Tracking 은 청크를 이어서 처리한다.
export const TRACK_CHUNK_FRAMES = 300;

// frame meta 를 모르는 소스(numFrames 0)의 목표 상한. 실제 한계는 영상 끝(done 조기 종료)이다.
export const FALLBACK_MAX_TRACK_FRAMES = 100_000;

// 컨디셔닝 keyframe 시드. FE 좌표(camera/episode/frameIndex)를 그대로 보관한다.
// connection 요청, echo 판정, 캐시 키가 모두 이 좌표를 쓴다. 서명 video URL 은 일시적이라 쓰지 않는다.
export type TrackingKeyframeSeed = {
  camera: string;
  episode: string;
  frameIndex: number;
  objects: AnnotationPromptObject[];
};

export type TrackingFrameObject = {
  objId: number;
  polygons: number[][][];
};

export type TrackingServerMessage =
  | { type: "start"; startFrame: number; endFrame: number }
  | {
      type: "frame";
      frameIndex: number;
      width: number;
      height: number;
      objects: TrackingFrameObject[];
      jpegBase64: string;
    }
  | { type: "done"; startFrame: number; endFrame: number }
  | { type: "error"; code: string; message: string | null };

type RawContour = { object_id?: unknown; polygons?: unknown };
type RawObject = { obj_id?: unknown; polygons?: unknown };

/**
 * 서버 텍스트 프레임을 파싱한다. 모르는 type 이나 JSON 이 아닌 값은 null 이다. 드라이버가 무시한다.
 * frame 의 objects 가 비면 contours(`{object_id, polygons: [{x,y}...][]}`)로 폴백한다
 * (원본 useTrackingStream 과 같다).
 */
export function parseTrackingMessage(raw: unknown): TrackingServerMessage | null {
  let data: Record<string, unknown>;
  try {
    const parsed: unknown = JSON.parse(String(raw));
    if (typeof parsed !== "object" || parsed === null) {
      return null;
    }
    data = parsed as Record<string, unknown>;
  } catch {
    return null;
  }

  switch (data.type) {
    case "start":
      return {
        type: "start",
        startFrame: asFrameIndex(data.start_frame),
        endFrame: asFrameIndex(data.end_frame),
      };
    case "frame": {
      const objects = parseFrameObjects(data.objects);
      return {
        type: "frame",
        frameIndex: asFrameIndex(data.frame_index),
        width: asPositiveInt(data.width),
        height: asPositiveInt(data.height),
        objects: objects.length > 0 ? objects : parseContourObjects(data.contours),
        jpegBase64: typeof data.frame_jpeg_base64 === "string" ? data.frame_jpeg_base64 : "",
      };
    }
    case "done":
      return {
        type: "done",
        startFrame: asFrameIndex(data.start_frame),
        endFrame: asFrameIndex(data.end_frame),
      };
    case "error":
      return {
        type: "error",
        code: typeof data.code === "string" ? data.code : "unknown",
        message: typeof data.message === "string" ? data.message : null,
      };
    default:
      return null;
  }
}

function parseFrameObjects(raw: unknown): TrackingFrameObject[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const objects: TrackingFrameObject[] = [];
  for (const entry of raw as RawObject[]) {
    const objId = Number(entry?.obj_id);
    if (!Number.isInteger(objId) || objId < 0 || !Array.isArray(entry.polygons)) {
      continue;
    }
    const polygons = entry.polygons.filter(
      (polygon): polygon is number[][] => Array.isArray(polygon) && polygon.length > 0,
    );
    if (polygons.length > 0) {
      objects.push({ objId, polygons });
    }
  }
  return objects;
}

function parseContourObjects(raw: unknown): TrackingFrameObject[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const objects: TrackingFrameObject[] = [];
  for (const entry of raw as RawContour[]) {
    const objId = Number(entry?.object_id);
    if (!Number.isInteger(objId) || objId < 0 || !Array.isArray(entry.polygons)) {
      continue;
    }
    const polygons: number[][][] = [];
    for (const polygon of entry.polygons as unknown[]) {
      if (!Array.isArray(polygon) || polygon.length === 0) {
        continue;
      }
      polygons.push(
        (polygon as { x?: unknown; y?: unknown }[]).map((vertex) => [
          Number(vertex?.x ?? 0),
          Number(vertex?.y ?? 0),
        ]),
      );
    }
    if (polygons.length > 0) {
      objects.push({ objId, polygons });
    }
  }
  return objects;
}

function asFrameIndex(value: unknown): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
}

function asPositiveInt(value: unknown): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
}

// ---- 청크 계산 ----

export type TrackingChunkPlan = {
  startFrame: number;
  maxFrames: number;
};

/** 다음 청크 계획. 목표(targetEnd, exclusive)에 이미 도달했으면 null. */
export function planNextChunk(chunkStart: number, targetEnd: number): TrackingChunkPlan | null {
  const remaining = targetEnd - chunkStart;
  if (remaining <= 0) {
    return null;
  }
  return { startFrame: chunkStart, maxFrames: Math.min(TRACK_CHUNK_FRAMES, remaining) };
}

export type TrackingChunkOutcome = {
  // 다음 청크 시작은 done.end_frame 그대로다. 1프레임이 겹치고, 청크 첫 frame 은 echo 라 버린다.
  nextStart: number;
  // 영상 끝, 목표 도달, 진행 없음 중 하나이면 청크 연결을 멈춘다.
  reachedEnd: boolean;
  // endFrame 이 시작보다 앞으로 갔는지. false 면 무한 루프를 막기 위해 항상 멈춘다.
  progressed: boolean;
};

/**
 * done 판정: 요청한 길이보다 짧게 끝났으면 영상 끝이다(원본 규칙과 같다).
 * 진행이 없으면 reachedEnd 와 관계없이 청크 연결을 멈춘다.
 */
export function judgeChunkDone(
  plan: TrackingChunkPlan,
  doneEndFrame: number,
  targetEnd: number,
): TrackingChunkOutcome {
  const progressed = doneEndFrame > plan.startFrame;
  const reachedVideoEnd = doneEndFrame < plan.startFrame + plan.maxFrames - 1;
  const reachedTarget = doneEndFrame >= targetEnd - 1;
  return {
    nextStart: doneEndFrame,
    reachedEnd: reachedVideoEnd || reachedTarget || !progressed,
    progressed,
  };
}

/**
 * 청크 첫 frame(컨디셔닝 echo)을 채택할지 판정한다. 다음 조건을 모두 만족할 때만 유효 데이터다
 * (원본 규칙과 같다): 컨디셔닝이 정확히 1개이고, 그것이 Tracking 대상 (camera, episode)의
 * chunkStart 다. 비교는 FE 가 보관한 좌표로만 한다. 서명 video URL 은 발급마다 바뀌어 비교
 * 기준이 될 수 없다.
 */
export function shouldAcceptEchoFrame(
  keyframes: readonly TrackingKeyframeSeed[],
  target: { camera: string; episode: string },
  chunkStart: number,
): boolean {
  return (
    keyframes.length === 1 &&
    keyframes[0].camera === target.camera &&
    keyframes[0].episode === target.episode &&
    keyframes[0].frameIndex === chunkStart
  );
}

// ---- 에러 문구 ----

export const TRACKING_ABORTED_MESSAGE = "Tracking 연결이 끊어졌습니다.";

/** holder/브리지 에러 코드 → 사용자 문구. 자동 재시도는 없다. 수동 재시도만 있다(원본과 같다). */
export function trackingErrorMessage(code: string, message?: string | null): string {
  switch (code) {
    case "busy":
      return "GPU가 다른 Tracking·미리보기를 처리 중입니다. 잠시 후 다시 시도해 주세요.";
    case "warming_up":
      return "Tracking Model 을 준비하고 있습니다. 잠시 후 다시 시도해 주세요.";
    case "no_holder":
      return "Tracking 서버에 연결하지 못했습니다.";
    case "track_failed":
      return "Tracking에 실패했습니다.";
    case "no_points":
      return "Tracking 대상 포인트가 없습니다.";
    case "bad_request":
      return "Tracking 요청이 잘못되었습니다.";
    default:
      return message !== undefined && message !== null && message !== ""
        ? `Tracking 오류: ${message}`
        : `Tracking 오류 (${code})`;
  }
}

// ---- 결과 캐시 키 ----

/**
 * (dataset, 대상 소스, 목표 길이, 컨디셔닝 내용)이 같으면 결과도 같다고 가정하는 키.
 * keyframe·object·좌표를 canonical 순서로 정렬해 입력 순서와 관계없는 키를 만든다.
 */
export function buildTrackingCacheKey(input: {
  datasetUuid: string;
  camera: string;
  episode: string;
  targetEnd: number;
  keyframes: readonly TrackingKeyframeSeed[];
}): string {
  const canonical = [...input.keyframes]
    .map((keyframe) => ({
      camera: keyframe.camera,
      episode: keyframe.episode,
      frameIndex: keyframe.frameIndex,
      objects: [...keyframe.objects]
        .map((object) => ({
          objId: Number(object.obj_id),
          points: object.points.map((point) => [Number(point[0]), Number(point[1])]),
          labels: object.labels.map((label) => (Number(label) === 1 ? 1 : 0)),
        }))
        .sort((a, b) => a.objId - b.objId),
    }))
    .sort(
      (a, b) =>
        a.camera.localeCompare(b.camera) ||
        a.episode.localeCompare(b.episode) ||
        a.frameIndex - b.frameIndex,
    );
  const digest = djb2(JSON.stringify(canonical));
  return `${input.datasetUuid}|${input.camera}|${input.episode}|${input.targetEnd}|${digest}`;
}

function djb2(text: string): string {
  let hash = 5381;
  for (let index = 0; index < text.length; index += 1) {
    hash = ((hash << 5) + hash + text.charCodeAt(index)) >>> 0;
  }
  return hash.toString(36);
}
